import { Typography } from "@/components/essentials/typography";
import { HStack, Flex, type FlexProps } from "@chakra-ui/react";
import { type ComponentProps, type FC } from "react";
import Link, { type LinkProps } from "next/link";
import { Box } from "@/components/essentials/box";
import { type IconAssets } from "@/components/essentials/icon/type";
import { Icon } from "@/components/essentials/icon";
import {
  settingsItemTemplateContractContractedAtTestId,
  settingsItemTemplateContractTestId,
  settingsItemTemplateItemTextTestId,
  settingsItemTemplateOnClickTestId,
  settingsItemTemplateTestId,
  settingsItemTemplateTitleTestId,
} from "./const";
import { Tag } from "@/components/essentials/tag";
import { tagTestId } from "@/components/essentials/form-control";
import { type AliasColors, type AliasColorScheme } from "@/styles/themes/colors/alias";
import { type ColorPalettes } from "@/styles/themes/colors/palettes";

export interface Item {
  readonly icon: IconAssets;
  readonly text: string;
  readonly href?: LinkProps["href"];
  readonly handleClick?: ComponentProps<typeof HStack>["onClick"];
  readonly target?: HTMLAnchorElement["target"];
  readonly rel?: HTMLAnchorElement["rel"];
  readonly color?: AliasColors | ColorPalettes;
  readonly tagItem?: TagItem;
}

export interface TagItem {
  readonly text: string;
  readonly colorScheme: AliasColorScheme;
}

export interface SettingsItemTemplateProps extends FlexProps {
  readonly title?: string;
  readonly items: Item[];
  readonly contractName?: string | undefined | null;
  readonly contractIsActive?: boolean | undefined | null;
}

export const SettingsItemTemplate: FC<SettingsItemTemplateProps> = ({
  title,
  items,
  contractName,
  contractIsActive,
  ...rest
}) => {
  return (
    <Flex
      data-testid={settingsItemTemplateTestId}
      flexDir="column"
      alignItems="start"
      gridGap={{ base: 6, sm: 6, md: 6, lg: 6, xl: 7, "2xl": 8 }}
      w="100%"
      {...rest}
    >
      {title && (
        <Typography data-testid={settingsItemTemplateTitleTestId} variant="display7" color="primary.main">
          {title}
        </Typography>
      )}
      {contractName && (
        <HStack data-testid={settingsItemTemplateContractTestId} pt="0.5rem" spacing="1rem">
          {contractIsActive ? (
            <Tag colorScheme="primary" variant="solid" borderRadius="1rem" p="0.5rem">
              契約中
            </Tag>
          ) : (
            <Tag colorScheme="gray" variant="solid" borderRadius="1rem" p="0.5rem">
              停止中
            </Tag>
          )}
          <Typography data-testid={settingsItemTemplateContractContractedAtTestId} variant="display6">
            {contractName}
          </Typography>
        </HStack>
      )}
      {items.map((item, index) => (
        <HStack
          data-testid={settingsItemTemplateOnClickTestId}
          ml={contractName ? "1rem" : "0.5rem"}
          key={index}
          cursor="pointer"
          onClick={item.handleClick}
        >
          <Icon as={item.icon} boxSize={{ base: 4, sm: 4, md: 5, lg: 5, xl: 6, "2xl": 6 }} />
          {item.href ? (
            <Box data-testid={`${settingsItemTemplateItemTextTestId}${item.href ? "Link" : ""}-${index}`}>
              <Typography
                target={item.target}
                rel={item.rel}
                as={Link}
                href={item.href}
                variant="text2"
                color="gray.dark"
              >
                {item.text}
              </Typography>
              {item.tagItem && (
                <Tag
                  typography="display8"
                  size="sm"
                  mb="0.5rem"
                  colorScheme={item.tagItem.colorScheme}
                  variant="outline"
                  marginLeft="1"
                  data-testid={tagTestId}
                >
                  {item.tagItem.text}
                </Tag>
              )}
            </Box>
          ) : (
            <Typography
              data-testid={`${settingsItemTemplateItemTextTestId}-${index}`}
              variant="text2"
              color={item.color ?? "gray.dark"}
            >
              {item.text}
            </Typography>
          )}
        </HStack>
      ))}
    </Flex>
  );
};
